// The week at a glance (wireframe 2): one row per day with dinner leading,
// B / L when breakfast or lunch is planned, today highlighted, a status line
// when a day is over its time budget or repeats a recent recipe; then
// Snacks and treats, the folded "Also this week" (preserves, medicinal),
// and the hand-off to the grocery list.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { RecipeTypeahead } from "../RecipeTypeahead";
import { RecipeTitleLink } from "../RecipeLinks";
import { COLORS, FONT_SIZE, LAYOUT, MISSING_RECIPE_LABEL, RADIUS, SPACE } from "../../lib/constants";
import { formatMinutes, orderedDays } from "../../lib/utils";
import { dayRow, recentRecipeIds } from "../../lib/planView";
import type { Day } from "../../lib/types";
import { smallLabel, supporting } from "../recipeStyles";
import { dashedButton } from "./planStyles";

type ListType = "snack" | "preserves" | "medicinal";

export function WeekOverview({ onOpenDay }: { onOpenDay: (day: Day) => void }) {
  const mp = useMealPlanner();
  const now = new Date();
  const recent = recentRecipeIds(mp.plansByWeek, mp.weekStart);
  const days = orderedDays(mp.planDefaults.weekStartDay) as Day[];

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3 }}>
      {(mp.weekData.title || mp.weeklyTotalTime > 0) && (
        <div>
          {mp.weekData.title && <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h2, fontWeight: 600 }}>{mp.weekData.title}</div>}
          <div style={supporting}>
            For {mp.weekData.servings || 4} · {formatMinutes(mp.weeklyTotalTime)} of cooking
          </div>
        </div>
      )}

      <nav aria-label="Days of the week" style={{ borderTop: `1px solid ${COLORS.line}` }}>
        {days.map((day) => {
          const row = dayRow(mp.plansByWeek, mp.weekStart, day, mp.recipeById, now, recent);
          const dinner = row.dinner;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onOpenDay(day)}
              aria-current={row.isToday ? "date" : undefined}
              style={{
                display: "grid",
                gridTemplateColumns: "56px minmax(0, 1fr) auto",
                gap: SPACE.xl,
                alignItems: "center",
                width: "100%",
                minHeight: 60,
                padding: "var(--space-sm) var(--space-xl)",
                border: "none",
                borderBottom: `1px solid ${COLORS.line}`,
                background: row.isToday ? COLORS.accentTint : "transparent",
                boxShadow: row.isToday ? `inset 4px 0 0 ${COLORS.accent}` : "none",
                textAlign: "left",
                cursor: "pointer",
                color: COLORS.ink,
                fontFamily: "var(--font-sans)",
                fontSize: FONT_SIZE.base,
              }}
            >
              <span style={{ fontSize: FONT_SIZE.sm, color: row.isToday ? COLORS.accent : COLORS.soft }}>
                <span style={{ display: "block", fontWeight: 700, color: row.isToday ? COLORS.accent : COLORS.ink }}>{row.isToday ? "Today" : day.slice(0, 3)}</span>
                {row.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
              <span style={{ minWidth: 0 }}>
                {dinner ? (
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600, lineHeight: 1.2 }}>
                    {dinner.isLeftover ? `Leftovers: ${dinner.title}` : dinner.title}
                  </span>
                ) : (
                  <span style={{ fontWeight: 700, color: COLORS.accent }}>+ Add dinner</span>
                )}
                {dinner?.isLeftover && <span style={{ ...supporting, display: "block" }}>{dinner.meta.replace(" · not on the grocery list", "")}</span>}
                {row.status && <span style={{ display: "block", fontSize: FONT_SIZE.sm, color: COLORS.caution, fontWeight: 700 }}>{row.status}</span>}
              </span>
              <span style={{ display: "flex", gap: SPACE.xs }}>
                {row.markers.map((m) => (
                  <span
                    key={m}
                    title={m === "B" ? "Breakfast planned" : "Lunch planned"}
                    style={{ fontSize: FONT_SIZE.xs, fontWeight: 700, border: `1px solid ${row.isToday ? COLORS.accent : COLORS.line}`, borderRadius: RADIUS.pill, padding: "0 var(--space-sm)", color: row.isToday ? COLORS.accent : COLORS.soft }}
                  >
                    {m}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
      </nav>
      <p style={{ ...supporting, margin: 0 }}>Each row shows dinner. B or L marks a planned breakfast or lunch. Tap a day to open it.</p>

      <WeekExtras />
    </div>
  );
}

// Under the days, on the phone overview and the desktop week: Snacks and
// treats, the folded "Also this week" (preserves, medicinal), and the
// hand-off to the grocery list.
export function WeekExtras() {
  const mp = useMealPlanner();
  const hasExtras = (mp.weekData.preserves || []).length + (mp.weekData.medicinal || []).length > 0;
  const [extrasOpen, setExtrasOpen] = useState(hasExtras);
  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3 }}>
      <WeekList listType="snack" title="Snacks and treats" />

      <section>
        <button
          type="button"
          aria-expanded={extrasOpen}
          onClick={() => setExtrasOpen((o) => !o)}
          style={{ width: "100%", minHeight: LAYOUT.tapTarget, display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.card, padding: "var(--space-md) var(--space-xl)", textAlign: "left", cursor: "pointer", color: COLORS.ink, fontFamily: "var(--font-sans)", fontSize: FONT_SIZE.base }}
        >
          <span>
            <strong>Also this week</strong>
            <span style={{ ...supporting, display: "block" }}>Preserves, Medicinal</span>
          </span>
          <span aria-hidden="true">{extrasOpen ? "▴" : "▾"}</span>
        </button>
        {extrasOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: SPACE.x3, marginTop: SPACE.xl }}>
            <WeekList listType="preserves" title="Preserves" />
            <WeekList listType="medicinal" title="Medicinal" />
          </div>
        )}
      </section>

      <Link
        to="/grocery"
        style={{ minHeight: 52, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 var(--space-xxl)", borderRadius: RADIUS.md, background: COLORS.accent, color: COLORS.onAccent, fontWeight: 700, textDecoration: "none" }}
      >
        <span>{mp.totalGroceryItems === 0 ? "Your grocery list is empty" : `${mp.totalGroceryItems} item${mp.totalGroceryItems === 1 ? "" : "s"} on your grocery list`}</span>
        <span aria-hidden="true">›</span>
      </Link>
    </div>
  );
}

const LIST_FIELD = { snack: "snacks", preserves: "preserves", medicinal: "medicinal" } as const;

// One of the week-long lists: chips with a remove button, then an add field.
function WeekList({ listType, title }: { listType: ListType; title: string }) {
  const mp = useMealPlanner();
  const [adding, setAdding] = useState(false);
  const entries = mp.weekData[LIST_FIELD[listType]] || [];
  const options = mp.recipes.filter((r) =>
    listType === "snack" ? (r.mealTypes || []).includes("snack") || r.recipeType === "candy" : r.recipeType === listType
  );
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <h2 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>{title}</h2>
      {entries.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.md }}>
          {entries.map((entry, i) => {
            const r = mp.recipeById(entry.recipeId);
            return (
              <span key={(entry as { id?: string }).id ?? i} style={{ display: "inline-flex", alignItems: "center", gap: SPACE.xs, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.pill, padding: "0 var(--space-xs) 0 var(--space-xl)", background: COLORS.card, minHeight: LAYOUT.tapTarget }}>
                {r ? <RecipeTitleLink recipeId={r.id} title={r.title} /> : MISSING_RECIPE_LABEL}
                <button
                  type="button"
                  aria-label={`Remove ${r?.title ?? "this"} from ${title.toLowerCase()}`}
                  onClick={() => mp.removeWeekItem(listType, i)}
                  style={{ width: 36, height: 36, border: "none", background: "transparent", color: COLORS.danger, fontSize: FONT_SIZE.h4, cursor: "pointer" }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      {adding ? (
        <RecipeTypeahead
          value=""
          onChange={(id: string) => {
            if (id) mp.addWeekItem(listType, id);
            setAdding(false);
          }}
          options={options}
          placeholder={`Add to ${title.toLowerCase()}…`}
          colors={mp.colors}
        />
      ) : (
        <button type="button" onClick={() => setAdding(true)} style={{ ...dashedButton, alignSelf: "flex-start" }}>
          + Add
        </button>
      )}
    </section>
  );
}
